import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  FileSignature,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Building2,
  Calendar,
  MapPin,
  DollarSign,
  User,
  Shield,
  Pen,
  AlertTriangle,
  HardHat,
  Info,
  Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PPE display mapping
const PPE_LABELS = {
  helmet: { label: "Helmet", icon: "🪖" },
  harness: { label: "Fall Protection/Harness", icon: "🦺" },
  gloves: { label: "Gloves", icon: "🧤" },
  safety_shoes: { label: "Safety Shoes", icon: "👢" },
  hi_vis: { label: "High-Visibility Clothing", icon: "🦺" },
  glasses: { label: "Safety Glasses", icon: "🥽" },
  hearing: { label: "Hearing Protection", icon: "🎧" },
  mask: { label: "Mask/Respirator", icon: "😷" },
};

export default function ProjectContractSign() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [user, setUser] = useState(null);
  
  // Checkboxes state
  const [checkboxes, setCheckboxes] = useState({
    project_details_read: false,
    sja_read: false,
    sja_hazards_understood: false,
    ppe_acknowledged: false,
    waiting_time_agreed: false,
    terms_accepted: false,
  });
  
  // Signature
  const [signatureName, setSignatureName] = useState("");

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      // Get current user
      const userRes = await axios.get(`${API}/auth/me`);
      setUser(userRes.data);
      
      // Get project details
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error loading project");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (key, checked) => {
    setCheckboxes(prev => ({ ...prev, [key]: checked }));
  };

  const allRequiredChecked = () => {
    const required = ["project_details_read", "sja_read", "sja_hazards_understood", "ppe_acknowledged", "terms_accepted"];
    
    // Add waiting time checkbox if enabled
    if (project?.waiting_time_terms?.enabled) {
      required.push("waiting_time_agreed");
    }
    
    return required.every(key => checkboxes[key]);
  };

  const handleSign = async () => {
    if (!allRequiredChecked()) {
      toast.error("Please check all required boxes to confirm you have read and understood all sections");
      return;
    }
    
    if (!signatureName.trim()) {
      toast.error("Please enter your full name as signature");
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/projects/${projectId}/sign-contract`, {
        user_id: user.user_id,
        user_name: user.name,
        checkboxes: checkboxes,
        signature_name: signatureName.trim(),
      });
      
      toast.success("Project contract signed successfully!");
      navigate(`/my-projects/${projectId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error signing contract");
    } finally {
      setSigning(false);
    }
  };

  const getRiskBadge = (level) => {
    const colors = {
      green: "bg-green-500/20 text-green-400 border-green-500/50",
      yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
      red: "bg-red-500/20 text-red-400 border-red-500/50",
    };
    const labels = { green: "Low", yellow: "Medium", red: "High" };
    return (
      <Badge className={`${colors[level]} border`}>
        {labels[level]} risk
      </Badge>
    );
  };

  // Check if user already signed
  const hasUserSigned = project?.contract_signatures?.some(
    sig => sig.user_id === user?.user_id
  );

  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title="Sign Project Contract">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout navItems={navItems} title="Sign Project Contract">
        <div className="text-center py-12">
          <p className="text-[#94A3B8]">Project not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title="Sign Project Contract">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="project-contract-sign">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        {/* Header */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-['Unbounded'] text-white flex items-center gap-3">
                  <FileSignature className="w-7 h-7 text-[#23F7DD]" />
                  Project Contract
                </CardTitle>
                <p className="text-[#94A3B8] mt-1">
                  Review all sections carefully before signing
                </p>
              </div>
              {hasUserSigned ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-lg px-4 py-2">
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Already Signed
                </Badge>
              ) : (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-lg px-4 py-2">
                  <Pen className="w-5 h-5 mr-2" />
                  Signature Required
                </Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Section 1: Project Details */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-[#23F7DD] rounded-full flex items-center justify-center text-[#050505] text-sm font-bold">1</span>
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="text-xl font-bold text-white">{project.title}</h3>
            
            {project.description && (
              <p className="text-[#94A3B8]">{project.description}</p>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <MapPin className="w-4 h-4 text-[#23F7DD]" />
                <span>{project.location_city}</span>
              </div>
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <Calendar className="w-4 h-4 text-[#23F7DD]" />
                <span>{project.start_date || "TBD"}</span>
              </div>
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <User className="w-4 h-4 text-[#23F7DD]" />
                <span>{project.required_crew_size} workers</span>
              </div>
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <Clock className="w-4 h-4 text-[#23F7DD]" />
                <span>{project.estimated_duration_hours || "?"} hours</span>
              </div>
            </div>

            {/* Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg mt-4">
              <Checkbox
                id="project_details_read"
                checked={checkboxes.project_details_read}
                onCheckedChange={(checked) => handleCheckboxChange("project_details_read", checked)}
                disabled={hasUserSigned}
                className="mt-1"
              />
              <label htmlFor="project_details_read" className="text-white cursor-pointer">
                <span className="font-medium">I have read and understood the project details</span>
                <span className="text-red-400 ml-1">*</span>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Safety Job Analysis (SJA) */}
        <Card className="bg-[#0A0A0A] border-red-500/30">
          <CardHeader className="bg-red-500/10">
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
              <Shield className="w-5 h-5 text-red-400" />
              Safety Job Analysis (SJA)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            {project.sja_form ? (
              <>
                {/* Job Description */}
                <div>
                  <h4 className="text-sm text-[#94A3B8] mb-2">Job Description</h4>
                  <p className="text-white p-3 bg-white/5 rounded-lg">
                    {project.sja_form.job_description || "No description provided"}
                  </p>
                </div>

                {/* Hazards */}
                <div>
                  <h4 className="text-sm text-[#94A3B8] mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    Identified Hazards ({project.sja_form.hazards?.length || 0})
                  </h4>
                  <div className="space-y-3">
                    {project.sja_form.hazards?.map((hazard, index) => (
                      <div key={hazard.hazard_id || index} className="p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{hazard.description}</span>
                          {getRiskBadge(hazard.risk_level)}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                          <div>
                            <span className="text-[#64748B]">Probability:</span>
                            <span className="text-white ml-2 capitalize">{hazard.probability}</span>
                          </div>
                          <div>
                            <span className="text-[#64748B]">Consequence:</span>
                            <span className="text-white ml-2 capitalize">{hazard.consequence}</span>
                          </div>
                        </div>
                        {hazard.preventive_measures && (
                          <div>
                            <span className="text-[#64748B] text-sm">Preventive Measures:</span>
                            <p className="text-green-400 text-sm mt-1">{hazard.preventive_measures}</p>
                          </div>
                        )}
                        {hazard.responsible_person && (
                          <div className="mt-2">
                            <span className="text-[#64748B] text-sm">Responsible:</span>
                            <span className="text-white text-sm ml-2">{hazard.responsible_person}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Measures */}
                {project.sja_form.additional_measures && (
                  <div>
                    <h4 className="text-sm text-[#94A3B8] mb-2">Additional Safety Measures</h4>
                    <p className="text-white p-3 bg-white/5 rounded-lg">
                      {project.sja_form.additional_measures}
                    </p>
                  </div>
                )}

                {/* Emergency Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div>
                    <span className="text-[#64748B] text-sm">Emergency Contact</span>
                    <p className="text-white">{project.sja_form.emergency_contact || "Not specified"}</p>
                  </div>
                  <div>
                    <span className="text-[#64748B] text-sm">Nearest Hospital</span>
                    <p className="text-white">{project.sja_form.nearest_hospital || "Not specified"}</p>
                  </div>
                  <div>
                    <span className="text-[#64748B] text-sm">First Aid Location</span>
                    <p className="text-white">{project.sja_form.first_aid_location || "Not specified"}</p>
                  </div>
                </div>

                {/* Checkboxes for SJA */}
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                    <Checkbox
                      id="sja_read"
                      checked={checkboxes.sja_read}
                      onCheckedChange={(checked) => handleCheckboxChange("sja_read", checked)}
                      disabled={hasUserSigned}
                      className="mt-1"
                    />
                    <label htmlFor="sja_read" className="text-white cursor-pointer">
                      <span className="font-medium">I have read the Safety Job Analysis (SJA)</span>
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                    <Checkbox
                      id="sja_hazards_understood"
                      checked={checkboxes.sja_hazards_understood}
                      onCheckedChange={(checked) => handleCheckboxChange("sja_hazards_understood", checked)}
                      disabled={hasUserSigned}
                      className="mt-1"
                    />
                    <label htmlFor="sja_hazards_understood" className="text-white cursor-pointer">
                      <span className="font-medium">I understand all identified hazards and preventive measures</span>
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-[#94A3B8]">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No SJA provided for this project</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Required PPE */}
        <Card className="bg-[#0A0A0A] border-yellow-500/30">
          <CardHeader className="bg-yellow-500/10">
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-black text-sm font-bold">3</span>
              <HardHat className="w-5 h-5 text-yellow-400" />
              Required Personal Protective Equipment (PPE)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {project.sja_form?.ppe_required?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {project.sja_form.ppe_required.map((ppeId) => {
                  const ppe = PPE_LABELS[ppeId] || { label: ppeId, icon: "🛡️" };
                  return (
                    <div
                      key={ppeId}
                      className="p-3 rounded-lg bg-green-500/20 border border-green-500/50 text-green-400 flex items-center gap-2"
                    >
                      <span className="text-xl">{ppe.icon}</span>
                      <span className="text-sm">{ppe.label}</span>
                      <CheckCircle2 className="w-4 h-4 ml-auto" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[#94A3B8] mb-6">No specific PPE requirements listed</p>
            )}

            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
              <Checkbox
                id="ppe_acknowledged"
                checked={checkboxes.ppe_acknowledged}
                onCheckedChange={(checked) => handleCheckboxChange("ppe_acknowledged", checked)}
                disabled={hasUserSigned}
                className="mt-1"
              />
              <label htmlFor="ppe_acknowledged" className="text-white cursor-pointer">
                <span className="font-medium">I acknowledge that I must wear the required PPE at all times during this project</span>
                <span className="text-red-400 ml-1">*</span>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Waiting Time Terms (if enabled) */}
        {project.waiting_time_terms?.enabled && (
          <Card className="bg-[#0A0A0A] border-orange-500/30">
            <CardHeader className="bg-orange-500/10">
              <CardTitle className="text-white flex items-center gap-2">
                <span className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">4</span>
                <Clock className="w-5 h-5 text-orange-400" />
                Waiting Time Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-[#64748B] text-sm">Maximum Waiting Hours (per person)</span>
                    <p className="text-white text-xl font-bold">
                      {project.waiting_time_terms.max_hours_per_person} hours
                    </p>
                  </div>
                  <div>
                    <span className="text-[#64748B] text-sm">Hourly Rate for Waiting</span>
                    <p className="text-[#23F7DD] text-xl font-bold">
                      {project.waiting_time_terms.hourly_rate} NOK/hour
                    </p>
                  </div>
                </div>
                
                {project.waiting_time_terms.reasons && (
                  <div>
                    <span className="text-[#64748B] text-sm">Reasons for Potential Waiting</span>
                    <p className="text-white mt-1">{project.waiting_time_terms.reasons}</p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-sm text-orange-400 flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> The waiting time compensation is <strong>already included</strong> in the total project price. 
                    If the project is completed earlier than expected, this amount will <strong>not be deducted</strong> from your payment.
                  </span>
                </p>
              </div>

              <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                <Checkbox
                  id="waiting_time_agreed"
                  checked={checkboxes.waiting_time_agreed}
                  onCheckedChange={(checked) => handleCheckboxChange("waiting_time_agreed", checked)}
                  disabled={hasUserSigned}
                  className="mt-1"
                />
                <label htmlFor="waiting_time_agreed" className="text-white cursor-pointer">
                  <span className="font-medium">I agree to the waiting time terms and conditions</span>
                  <span className="text-red-400 ml-1">*</span>
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 5: Final Agreement & Signature */}
        <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
          <CardHeader className="bg-[#23F7DD]/10">
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-[#23F7DD] rounded-full flex items-center justify-center text-[#050505] text-sm font-bold">
                {project.waiting_time_terms?.enabled ? "5" : "4"}
              </span>
              <Pen className="w-5 h-5 text-[#23F7DD]" />
              Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Terms checkbox */}
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
              <Checkbox
                id="terms_accepted"
                checked={checkboxes.terms_accepted}
                onCheckedChange={(checked) => handleCheckboxChange("terms_accepted", checked)}
                disabled={hasUserSigned}
                className="mt-1"
              />
              <label htmlFor="terms_accepted" className="text-white cursor-pointer">
                <span className="font-medium">
                  I accept all terms and conditions of this project contract. I confirm that all information I have provided is accurate 
                  and I will comply with all safety requirements.
                </span>
                <span className="text-red-400 ml-1">*</span>
              </label>
            </div>

            {/* Signature input */}
            <div className="space-y-2">
              <label className="text-[#94A3B8] text-sm">
                Type your full legal name as signature
                <span className="text-red-400 ml-1">*</span>
              </label>
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Enter your full name"
                className="bg-black/50 border-white/10 text-white text-lg font-medium h-12"
                disabled={hasUserSigned}
              />
              {signatureName && (
                <div className="p-4 bg-white/5 rounded-lg border border-dashed border-white/20">
                  <p className="text-[#64748B] text-xs mb-1">Signature Preview</p>
                  <p className="text-white text-2xl font-serif italic">{signatureName}</p>
                </div>
              )}
            </div>

            {/* Already signed info */}
            {hasUserSigned && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                  <div>
                    <p className="text-green-400 font-medium">Contract Already Signed</p>
                    <p className="text-[#94A3B8] text-sm">
                      You signed this contract on{" "}
                      {new Date(
                        project.contract_signatures.find(s => s.user_id === user?.user_id)?.signed_at
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Sign button */}
            {!hasUserSigned && (
              <Button
                onClick={handleSign}
                disabled={signing || !allRequiredChecked() || !signatureName.trim()}
                className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold py-6 text-lg"
              >
                {signing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                    Signing...
                  </span>
                ) : (
                  <>
                    <FileSignature className="w-5 h-5 mr-2" />
                    Sign Project Contract
                  </>
                )}
              </Button>
            )}

            {!hasUserSigned && !allRequiredChecked() && (
              <p className="text-center text-yellow-400 text-sm">
                <Info className="w-4 h-4 inline mr-1" />
                Please check all required boxes above to enable signing
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
